import { useEffect, useMemo, useState } from "react"
import { Button, Input, message } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import StatusTag from "../../../components/StatusTag/StatusTag"
import DeviceCell from "../../../components/DeviceCell/DeviceCell"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { renderDate } from "../../../Utils/utils"
import { appStatusLabel, normalizeAppStatus } from "../../../Utils/appStatus"
import "../AppManagement.scss"
import "../../Operations/Operations.scss"

const KPI_TONES = {
  all: { tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
  pending: { tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
  installing: { tone: "#0891b2", soft: "rgba(56, 189, 248, 0.14)" },
  installed: { tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
  failed: { tone: "#dc2626", soft: "rgba(248, 113, 113, 0.14)" },
  rejected: { tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
  uninstalled: { tone: "#7c3aed", soft: "rgba(167, 139, 250, 0.14)" },
}

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Requested" },
  { key: "installing", label: "Installing" },
  { key: "installed", label: "Installed" },
  { key: "rejected", label: "Rejected" },
  { key: "failed", label: "Failed" },
]

const AllApps = () => {
  const page = "App Inventory"
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("all")
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
  })

  const load = async () => {
    setLoading(true)
    try {
      const response = await apiClient("packages/get")
      const grouped = response?.result || []
      const flat = grouped.flatMap((host) =>
        (host.appsLists || []).flatMap((group) =>
          (group.apps || []).map((app, index) => ({
            key: `${host.hostname}-${group.status}-${app.appName}-${index}`,
            hostname: host.hostname,
            status: normalizeAppStatus(group.status),
            rawStatus: group.status,
            appName: app.appName,
            packageId: app.packageId || app.appData?.packageId,
            source: app.source || app.appData?.source,
            version: app.version,
            iconUrl: app.iconUrl,
            requestedEmail: app.requestedEmail,
            createdAt: app.createdAt,
            deviceId: app.deviceId,
          }))
        )
      )
      setRows(flat)
    } catch (err) {
      console.error("Failed to load app inventory", err)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (status !== "all" && row.status !== status) return false
      if (!search) return true
      const blob = [row.hostname, row.appName, row.version, row.requestedEmail]
        .join(" ")
        .toLowerCase()
      return blob.includes(search)
    })
  }, [rows, status, search])

  const summary = useMemo(() => {
    const counts = { all: filtered.length, pending: 0, installing: 0, installed: 0, rejected: 0, failed: 0 }
    const base = search
      ? rows.filter((row) =>
          [row.hostname, row.appName, row.version, row.requestedEmail]
            .join(" ")
            .toLowerCase()
            .includes(search)
        )
      : rows
    counts.all = base.length
    base.forEach((row) => {
      if (counts[row.status] !== undefined) counts[row.status] += 1
    })
    return counts
  }, [rows, search])

  const pageRows = filtered.slice(
    (pagination.page - 1) * pagination.pageSize,
    pagination.page * pagination.pageSize
  )

  const columns = [
    {
      title: "App",
      dataIndex: "appName",
      key: "appName",
      render: (name, record) => (
        <div className="app-mgmt__app">
          {record.iconUrl ? (
            <img src={record.iconUrl} alt="" className="app-mgmt__icon" />
          ) : null}
          <span className="app-mgmt__name">{name || "—"}</span>
        </div>
      ),
    },
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 280,
      render: (hostname, record) => (
        <DeviceCell
          record={{ _id: record.deviceId, deviceStatus: record.deviceStatus }}
          hostname={hostname}
          user={record.requestedEmail}
        />
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 120,
      render: (version) => version || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (value) => <StatusTag status={appStatusLabel(value)} />,
    },
    {
      title: "When",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (value) => renderDate(value),
    },
    {
      title: "",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <span>
          <Button
            size="small"
            disabled={!record.hostname || !record.packageId}
            onClick={async () => {
              try {
                await apiClient("devices/catalog-uninstall", "POST", {
                  hostname: record.hostname,
                  packageId: record.packageId,
                  source: record.source,
                  appName: record.appName,
                })
                message.success(`Uninstall queued on ${record.hostname}`)
              } catch (err) {
                message.error(err.message || "Could not queue uninstall")
              }
            }}
          >
            Uninstall
          </Button>{" "}
          <Button
            size="small"
            disabled={!record.hostname || !record.packageId}
            onClick={async () => {
              try {
                await apiClient("devices/catalog-uninstall", "POST", {
                  hostname: record.hostname,
                  packageId: record.packageId,
                  source: record.source,
                  appName: record.appName,
                  replace: true,
                })
                message.success(`Replace queued on ${record.hostname}`)
              } catch (err) {
                message.error(err.message || "Could not queue replace")
              }
            }}
          >
            Replace
          </Button>
        </span>
      ),
    },
  ]

  return (
    <PanelLayout
      title="App inventory"
      subtitle="Every app request and install across the fleet by device and status, with uninstall and replace actions."
      actions={
        <>
          <Input
            className="ops-search"
            prefix={<SearchOutlined />}
            allowClear
            placeholder="Search app, device, or email"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
          />
          <Button onClick={load} loading={loading}>
            Refresh
          </Button>
        </>
      }
    >
      <div className="ops-kpis">
        {STATUS_FILTERS.map((item) => {
          const tones = KPI_TONES[item.key] || KPI_TONES.all
          return (
            <button
              type="button"
              key={item.key}
              className={`ops-kpi${status === item.key ? " is-active" : ""}`}
              style={{ "--act-tone": tones.tone, "--act-soft": tones.soft }}
              onClick={() => {
                setStatus(item.key)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            >
              <div>
                <span className="ops-kpi-label">{item.label}</span>
                <strong>{summary[item.key] ?? 0}</strong>
              </div>
            </button>
          )
        })}
      </div>

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {filtered.length} app{filtered.length === 1 ? "" : "s"}
          {status !== "all"
            ? ` · ${STATUS_FILTERS.find((item) => item.key === status)?.label}`
            : ""}
        </span>
        <div className="dashboard-filter-chips">
          <span>Status</span>
          {STATUS_FILTERS.map((item) => (
            <Button
              key={item.key}
              size="small"
              type={status === item.key ? "primary" : "default"}
              onClick={() => {
                setStatus(item.key)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      <TableBox
        page={page}
        title={null}
        loading={loading}
        columns={columns}
        className="dashboard-inventory-table"
        data={pageRows}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: filtered.length,
          showSizeChanger: true,
        }}
        onChange={(pager) =>
          setPagination({
            page: pager.current,
            pageSize: pager.pageSize,
          })
        }
      />
    </PanelLayout>
  )
}

export default AllApps
